import {
  createContext,
  type ReactNode,
  type Reducer,
  useContext,
  useEffect,
  useReducer,
} from "react";

const ADMIN_STORE_NAME = "adminStore";

interface AdminUser {
  name: string;
  image?: string;
}

interface AdminStore {
  adminLoggedIn: Readonly<boolean>;
  adminToken: string | null;
  adminUser: AdminUser | null;
}

const getDefaultAdminStore = (): AdminStore => ({
  adminLoggedIn: false,
  adminToken: null,
  adminUser: null,
});

const getStoredAdminState = () => {
  if (typeof localStorage === "undefined") return getDefaultAdminStore();
  const storedState = localStorage.getItem(ADMIN_STORE_NAME);
  return storedState ? JSON.parse(storedState) : getDefaultAdminStore();
};

const RESET_ADMIN_STORE = "RESET_ADMIN_STORE";
const SET_ADMIN_LOGGED_IN = "SET_ADMIN_LOGGED_IN";
const SET_ADMIN_TOKEN = "SET_ADMIN_TOKEN";
const SET_ADMIN_USER = "SET_ADMIN_USER";
const RESET = "RESET";

type AdminAction = {
  type:
    | typeof RESET_ADMIN_STORE
    | typeof SET_ADMIN_LOGGED_IN
    | typeof SET_ADMIN_TOKEN
    | typeof SET_ADMIN_USER
    | typeof RESET;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
};

const adminStoreReducer: Reducer<AdminStore, AdminAction> = (state, action) => {
  switch (action.type) {
    case RESET_ADMIN_STORE:
      return getDefaultAdminStore();
    case SET_ADMIN_LOGGED_IN:
      return { ...state, adminLoggedIn: action.payload };
    case SET_ADMIN_TOKEN:
      return { ...state, adminToken: action.payload };
    case SET_ADMIN_USER:
      return { ...state, adminUser: action.payload };
    case RESET:
      return getDefaultAdminStore();
    default:
      return state;
  }
};

type AdminStoreType = AdminStore & {
  resetAdminStore: () => void;
  setAdminLoggedIn: (_loggedIn: boolean) => void;
  setAdminToken: (_token: string | null) => void;
  setAdminUser: (_user: AdminUser | null) => void;
  reset: () => void;
};

const AdminStoreContext = createContext<AdminStoreType>({
  ...getDefaultAdminStore(),
  resetAdminStore: () => {},
  setAdminLoggedIn: (_loggedIn: boolean) => {},
  setAdminToken: (_token: string | null) => {},
  setAdminUser: (_user: AdminUser | null) => {},
  reset: () => {},
});

export const useAdminStore = () => {
  return useContext(AdminStoreContext);
};

type AdminStoreProviderProps = {
  children: ReactNode;
};

export const AdminStoreProvider = ({ children }: AdminStoreProviderProps) => {
  const [state, dispatch] = useReducer(
    adminStoreReducer,
    getStoredAdminState(),
  );

  useEffect(() => {
    localStorage.setItem(ADMIN_STORE_NAME, JSON.stringify(state));
  }, [state]);

  // Define actions
  const resetAdminStore = () => dispatch({ type: RESET_ADMIN_STORE });
  const setAdminLoggedIn = (loggedIn: boolean) =>
    dispatch({ type: SET_ADMIN_LOGGED_IN, payload: loggedIn });
  const setAdminToken = (token: string | null) =>
    dispatch({ type: SET_ADMIN_TOKEN, payload: token });
  const setAdminUser = (user: AdminUser | null) =>
    dispatch({ type: SET_ADMIN_USER, payload: user });
  const reset = () => dispatch({ type: RESET });

  // Context value
  const contextValue = {
    ...state,
    resetAdminStore,
    setAdminLoggedIn,
    setAdminToken,
    setAdminUser,
    reset,
  };

  return (
    <AdminStoreContext.Provider value={contextValue}>
      {children}
    </AdminStoreContext.Provider>
  );
};
